import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import ResponsiveModal from 'presentation/shared/components/ResponsiveModal'
import ProceduresTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/ProceduresTable'
import ProceduresList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/ProceduresList'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { ProcedureV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { ProcedureModel } from 'domain/entities/surgical-order-model'
import { useServices } from 'presentation/hooks/use-services'
import { ScrollableTableContainer } from '../components/styles'

import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { DataSource } from 'common/enum/data-source'
import { useStores } from 'presentation/hooks/use-stores'

type AddProcedureResponsiveModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (procedures: ProcedureV2Props[]) => void
  procedures: ProcedureV2Props[]
  allProcedures: ProcedureV2Props[]
}

type ProcedureSuggestion = {
  label: string
  value: ProcedureModel
}

const AddProcedureResponsiveModal = ({
  isOpen,
  onClose,
  onConfirm,
  procedures,
  allProcedures
}: AddProcedureResponsiveModalProps) => {
  const surgicalOrderService = useServices().surgicalOrder
  const currentUser = useStores().currentAccount.getCurrentAccount().user
  const [procedureSearchTerm, setProcedureSearchTerm] = useState('')
  const [proceduresList, setProceduresList] = useState<ProcedureSuggestion[]>(
    []
  )
  const [selectedProcedure, setSelectedProcedure] =
    useState<ProcedureModel | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [addedProcedures, setAddedProcedures] =
    useState<ProcedureV2Props[]>(procedures)

  useEffect(() => {
    if (isOpen) {
      setAddedProcedures(procedures)
    }
  }, [isOpen])

  const resetModalState = () => {
    setProcedureSearchTerm('')
    setProceduresList([])
    setSelectedProcedure(null)
    setQuantity('1')
  }

  const handleClose = () => {
    resetModalState()
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(addedProcedures)
    resetModalState()
  }

  const getProcedures = async (searchTerm: string) => {
    try {
      if (searchTerm) {
        let searchParams

        if (isNaN(Number(searchTerm))) {
          searchParams = { name: searchTerm }
        } else {
          searchParams = { code: searchTerm }
        }

        const searchProcedures = await surgicalOrderService.searchProcedures(
          searchParams
        )

        const suggestionsProcedures = searchProcedures.map((procedure) => ({
          label: `${procedure.tuss_id} - ${procedure.description}`,
          value: procedure
        }))

        setProceduresList(suggestionsProcedures)
      } else {
        setProceduresList([])
      }
    } catch (error: any) {
      toast.error('Ocorreu um erro ao buscar procedimentos')
      setProceduresList([])
    }
  }

  const handleProcedureSelect = (procedure: ProcedureSuggestion) => {
    const procedureCode = procedure.value.tuss_id

    const procedureExistsInExternalTable =
      allProcedures.some((proc) => proc.code === procedureCode) ||
      addedProcedures.some((proc) => proc.code === procedureCode)

    if (procedureExistsInExternalTable) {
      toast.error('Este procedimento já está na listagem')
      setProceduresList([])
      return
    }

    setSelectedProcedure(procedure.value)
    setProcedureSearchTerm(procedure.label)
    setProceduresList([])
  }

  const handleProcedureInputChange = (value: string) => {
    setProcedureSearchTerm(value)
    if (!value) {
      setSelectedProcedure(null)
    }
  }

  const handleAddProcedure = () => {
    if (!selectedProcedure || !quantity || parseInt(quantity) <= 0) {
      return
    }

    const procedureExists = addedProcedures.some(
      (proc) => proc.code === selectedProcedure.tuss_id
    )

    if (procedureExists) {
      toast.error('Este procedimento já foi adicionado')
      return
    }

    const newProcedure: ProcedureV2Props = {
      code: selectedProcedure.tuss_id || selectedProcedure.code,
      description: selectedProcedure.description,
      quantity: parseInt(quantity, 10),
      is_main: false,
      source: DataSource.DOCTOR,
      is_selected: true,
      surgery_id: selectedProcedure.surgery_id,
      pro_fat_id: selectedProcedure.pro_fat_id,
      doctor_name: currentUser.name
    }

    setAddedProcedures((prev) => [...prev, newProcedure])
    setSelectedProcedure(null)
    setProcedureSearchTerm('')
    setQuantity('1')
  }

  const handleDeleteProcedure = (procedureCode: string) => {
    setAddedProcedures((prev) =>
      prev.filter((proc) => proc.code !== procedureCode)
    )
    toast.success('Procedimento removido com sucesso')
  }

  const handleQuantityChangeProcedure = (
    procedureCode: string,
    newQuantity: number
  ) => {
    setAddedProcedures((prev) =>
      prev.map((proc) =>
        proc.code === procedureCode ? { ...proc, quantity: newQuantity } : proc
      )
    )
  }

  const handleToggleSelectionProcedure = (procedureCode: string) => {
    setAddedProcedures((prev) =>
      prev.map((proc) =>
        proc.code === procedureCode
          ? { ...proc, is_selected: !proc.is_selected }
          : proc
      )
    )
  }

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '')

    if (value === '') {
      setQuantity('')
      return
    }

    const numericValue = parseInt(value, 10)
    if (!isNaN(numericValue) && numericValue > 0 && numericValue <= 99) {
      setQuantity(value)
    }
  }

  const handleQuantityBlur = () => {
    if (quantity === '' || parseInt(quantity, 10) <= 0) {
      setQuantity('1')
    }
  }

  const isAddButtonDisabled =
    !selectedProcedure || !quantity || parseInt(quantity) <= 0
  const isConfirmButtonDisabled = addedProcedures.length === 0

  return (
    <ResponsiveModal
      isOpen={isOpen}
      title="Adicionar procedimentos"
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmButtonLabel="Salvar e prosseguir"
      disableConfirmButton={isConfirmButtonDisabled}
    >
      {(isMobile) => (
        <>
          <S.SearchContainer>
            <S.AutoCompleteWrapper>
              <AutoCompleteNew
                name="procedure"
                placeholder="Digite o código TUSS ou nome"
                onInputChange={handleProcedureInputChange}
                returnFullSuggestion
                label="Código TUSS ou nome"
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="textSecondary"
                labelOverlapping
                iconPosition="insideLeft"
                icon={<SearchIcon />}
                minCharactersSearch={1}
                suggestions={proceduresList || []}
                onSuggestionClick={handleProcedureSelect}
                value={procedureSearchTerm}
                onType={getProcedures}
                debounceDelay={800}
                useClearableInput
                required
                requiredColor="textSecondary"
              />
            </S.AutoCompleteWrapper>

            <S.QuantityWrapper>
              <TextFieldNew
                name="quantity"
                label="Quantidade"
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="textSecondary"
                labelOverlapping
                placeholder="1"
                value={quantity}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                type="number"
                maxLength={2}
                required
                requiredColor="textSecondary"
              />
            </S.QuantityWrapper>
          </S.SearchContainer>

          <ButtonNew
            outlined
            size="large"
            fullWidth
            onClick={handleAddProcedure}
            disabled={isAddButtonDisabled}
          >
            Adicionar procedimento
          </ButtonNew>

          {!!addedProcedures && addedProcedures.length > 0 && (
            <>
              <HeadingNew
                as="h2"
                color="textSecondary"
                weight="bold"
                size="xsmall"
                style={{
                  textAlign: 'start',
                  marginTop: '2rem',
                  marginBottom: '0.5rem'
                }}
              >
                Procedimentos adicionados:
              </HeadingNew>

              <ScrollableTableContainer>
                {isMobile ? (
                  <ProceduresList
                    initialRows={addedProcedures}
                    onDelete={handleDeleteProcedure}
                    onQuantityChange={handleQuantityChangeProcedure}
                    onToggleSelection={handleToggleSelectionProcedure}
                    hideSections
                    formatDescription
                  />
                ) : (
                  <ProceduresTable
                    initialRows={addedProcedures}
                    onDelete={handleDeleteProcedure}
                    onQuantityChange={handleQuantityChangeProcedure}
                    onToggleSelection={handleToggleSelectionProcedure}
                    hideSections
                    formatDescription
                  />
                )}
              </ScrollableTableContainer>
            </>
          )}
        </>
      )}
    </ResponsiveModal>
  )
}

export default AddProcedureResponsiveModal
